'use client';

import type { TooltipPlacementType } from '@/app/components/overlays/tooltip';
import { DEFAULT_PLACEMENT } from '@/app/components/overlays/tooltip';

const ROW_GAP = 14;
const PAGE_PADDING = 16;

type YPlacement = 'top' | 'bottom';
type XPlacement = 'left' | 'right' | 'center';

export const getAutoPlacement = (
  tooltipElement: HTMLElement,
  triggerElement: HTMLElement,
  placement: TooltipPlacementType
): TooltipPlacementType => {
  const triggerRect = triggerElement.getBoundingClientRect();
  const tooltipRect = tooltipElement.getBoundingClientRect();
  const viewportWidth =
    window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;

  const canDisplay = {
    top: triggerRect.top - tooltipRect.height - ROW_GAP - PAGE_PADDING > 0,
    bottom:
      triggerRect.bottom + tooltipRect.height + ROW_GAP + PAGE_PADDING <
      viewportHeight,
    left: triggerRect.left + tooltipRect.width + PAGE_PADDING < viewportWidth,
    right: triggerRect.right - tooltipRect.width - PAGE_PADDING > 0,
    center:
      triggerRect.left +
        triggerRect.width / 2 -
        (tooltipRect.width / 2 + PAGE_PADDING) >
        0 &&
      triggerRect.right +
        triggerRect.width / 2 +
        (tooltipRect.width / 2 + PAGE_PADDING) <
        viewportWidth,
  };

  const validPlacement = placement ?? DEFAULT_PLACEMENT;
  const [yPlacement, xPlacement] = validPlacement.split('-') as [
    YPlacement,
    XPlacement,
  ];

  // If a passing placement is specified, return this first
  if (canDisplay[yPlacement] && canDisplay[xPlacement]) {
    return placement;
  }

  let safeYPlacement: YPlacement;
  if (yPlacement === 'top') {
    safeYPlacement = canDisplay.top ? 'top' : 'bottom';
  } else {
    safeYPlacement = canDisplay.bottom ? 'bottom' : 'top';
  }

  if (canDisplay.right) {
    return `${safeYPlacement}-right`;
  }

  if (canDisplay.left) {
    return `${safeYPlacement}-left`;
  }

  return `${safeYPlacement}-center`;
};
